import { Badge } from "@/components/ui/badge";
import { statusTone } from "@/features/utils/colors";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import type { AdminEntitySchema, AdminRow } from "../types";

/**
 * Column generation for the back-office's generic grid.
 *
 * The grid never knows what it is showing: it asks the server for a table's
 * columns and turns each one into a `ColumnDef` whose renderer is picked from
 * the column's *name and SQL type*. Adding a column to an entity therefore adds
 * it to the back-office, with a sensible cell, without touching any of this.
 *
 * The only hand-written knowledge here is {@link FOREIGN_KEYS} — which id
 * columns point at which entity — because that is what turns a wall of opaque
 * identifiers into something you can click through.
 */

/** `column name → entity table it references`, used to link rows together. */
export const FOREIGN_KEYS: Record<string, string> = {
  client_id: "clients",
  target_client_id: "clients",
  user_id: "users",
  created_by: "users",
  updated_by: "users",
  deleted_by: "users",
  invited_by: "users",
  reviewed_by: "users",
  member_id: "client_members",
  customer_id: "customers",
  session_id: "sessions",
  result_session_id: "sessions",
  from_session_id: "sessions",
  agent_id: "client_agents",
  tool_id: "tools",
  form_id: "qualification_forms",
  document_id: "ragdocuments",
  calendar_id: "calendars",
  schedule_id: "calendar_schedules",
  service_id: "services",
  connector_id: "sessions_connectors",
  device_id: "sessions_devices",
  account_id: "external_accounts",
  primary_calendar_account_id: "external_accounts",
};

/** Columns shown first, in this order, when the table has them. */
const LEADING_COLUMNS = ["id", "client_id", "created_at"];

/** Columns pushed to the end — audit plumbing nobody scans a table for. */
const TRAILING_COLUMNS = [
  "created_by",
  "updated_at",
  "updated_by",
  "is_deleted",
  "searchable",
  "operation",
  "operation_timestamp",
];

/** Hidden unless the user asks for them, on top of the entity's own list. */
const DEFAULT_HIDDEN_COLUMNS = [
  "created_by",
  "updated_by",
  "updated_at",
  "is_deleted",
  "searchable",
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Our own ids: base36 timestamp + random tail (see platform/db/utils.ts). */
const SHORT_ID_RE = /^[0-9a-z]{12,13}$/;

export const looksLikeId = (value: string): boolean =>
  UUID_RE.test(value) || SHORT_ID_RE.test(value);

/**
 * A number named like a date is a date — epoch milliseconds, which is how the schema
 * stores them. Most of those columns come out of `columnsFromEntity` as FLOAT (a
 * `created_at: number = 0` default is just a number to it), a few are declared BIGINT,
 * so the name decides and the type only has to be numeric.
 */
export const isTimestampColumn = (name: string, type: string): boolean =>
  /(BIGINT|INT|FLOAT|DOUBLE|REAL|NUMERIC|DECIMAL)/i.test(type) &&
  !/\[\]$/.test(type) &&
  /(_at|_time|_timestamp|^timestamp|_date|_until|_since|^expires|_expires|_expiry|_seen|_ts)$/.test(
    name,
  );

/** Columns whose numbers are amounts of time or things, never an instant. */
const NOT_AN_INSTANT = /(_ms|_s|_sec|_seconds|duration|delay|count|total|size|bytes|tokens|amount|price|cost|_usd|credits|length|limit|quota)$/i;

/** 2010-01-01 → 2050-01-01, in ms. A number in there is an epoch-ms instant. */
const EPOCH_MS_MIN = 1262304000000;
const EPOCH_MS_MAX = 2524608000000;

/**
 * Is this value an instant? By name first (see {@link isTimestampColumn}); failing
 * that, an epoch-ms-sized number on a column not named like a duration or a count —
 * which catches the timestamp columns whose names follow no convention.
 */
export const isTimestampValue = (
  name: string,
  type: string,
  value: unknown,
): boolean => {
  if (isTimestampColumn(name, type)) return true;
  if (NOT_AN_INSTANT.test(name)) return false;
  const n = typeof value === "number" ? value : NaN;
  return Number.isInteger(n) && n >= EPOCH_MS_MIN && n < EPOCH_MS_MAX;
};

export const isStatusColumn = (name: string): boolean =>
  /(^|_)(status|state|role|type|kind|medium|operation|provider|category|verdict|grade)$/.test(
    name,
  );

/** `created_at` → `Created at`, and the accents survive. */
export const humanizeColumn = (name: string): string => {
  const words = name.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Milliseconds since epoch, whatever the driver handed back. */
export const asTimestamp = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** "il y a 3 min", "dans 2 j" — the hint next to an exact date. */
export const relativeTime = (ms: number): string => {
  const diff = ms - Date.now();
  const abs = Math.abs(diff);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 365 * 86400000],
    ["month", 30 * 86400000],
    ["day", 86400000],
    ["hour", 3600000],
    ["minute", 60000],
  ];
  const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
  for (const [unit, size] of units) {
    if (abs >= size) return rtf.format(Math.round(diff / size), unit);
  }
  return rtf.format(Math.round(diff / 1000), "second");
};

/** Everything about an instant, for a tooltip: the exact date, how long ago, the raw value. */
export const timestampTitle = (value: unknown): string | undefined => {
  const ms = asTimestamp(value);
  if (ms === null) return undefined;
  try {
    return `${format(new Date(ms), "EEEE dd/MM/yyyy HH:mm:ss.SSS", { locale: fr })} (${relativeTime(ms)})\n${ms}`;
  } catch {
    return String(value);
  }
};

export const formatTimestamp = (
  value: unknown,
  withSeconds = false,
): string => {
  const ms = asTimestamp(value);
  if (ms === null) return "—";
  try {
    return format(
      new Date(ms),
      withSeconds ? "dd/MM/yyyy HH:mm:ss" : "dd/MM/yyyy HH:mm",
    );
  } catch {
    return String(value);
  }
};

/** A one-line preview of any value, for tooltips and CSV-ish contexts. */
export const previewValue = (value: unknown, max = 120): string => {
  if (value === null || value === undefined) return "—";
  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

/** The `/admin/explorer/...` route for a referenced row, when we know the entity. */
export const foreignKeyPath = (
  column: string,
  value: string,
): string | null => {
  const table = FOREIGN_KEYS[column];
  if (!table || !value) return null;
  if (table === "clients") return `/admin/tenants/${value}`;
  return `/admin/explorer/${table}/${encodeURIComponent(value)}`;
};

/** A JSON value, behind a popover so one wide column cannot wreck the row. */
function JsonCell({ value }: { value: unknown }) {
  const isArray = Array.isArray(value);
  const size = isArray
    ? (value as unknown[]).length
    : Object.keys(value as object).length;

  if (size === 0) {
    return (
      <span className="text-muted-foreground text-xs">
        {isArray ? "[]" : "{}"}
      </span>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs font-mono"
          onClick={(event) => event.stopPropagation()}
        >
          {isArray ? `[${size}]` : `{${size}}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[32rem] max-w-[90vw]"
        onClick={(event) => event.stopPropagation()}
      >
        <pre className="text-xs bg-muted p-2 rounded-sm max-h-80 overflow-auto whitespace-pre-wrap break-all">
          {JSON.stringify(value, null, 2)}
        </pre>
      </PopoverContent>
    </Popover>
  );
}

/** An id with a link to the row it references, when there is one. */
function IdCell({ column, value }: { column: string; value: string }) {
  const path = foreignKeyPath(column, value);

  if (!path) {
    return (
      <span className="font-mono text-xs text-muted-foreground" title={value}>
        {value.length > 14 ? `${value.slice(0, 12)}…` : value}
      </span>
    );
  }

  return (
    <Link
      to={path}
      onClick={(event) => event.stopPropagation()}
      className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
      title={value}
    >
      {value.length > 14 ? `${value.slice(0, 12)}…` : value}
      <ArrowSquareOutIcon className="h-3 w-3 shrink-0" />
    </Link>
  );
}

/** The renderer for one column, chosen from its name and SQL type. */
export function renderCell(
  columnName: string,
  type: string,
  value: unknown,
): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }

  if (typeof value === "boolean") {
    return (
      <Badge color={value ? "green" : undefined} variant="outline">
        {value ? "oui" : "non"}
      </Badge>
    );
  }

  if (isTimestampValue(columnName, type, value)) {
    return (
      <span
        className="text-xs text-muted-foreground whitespace-nowrap"
        title={timestampTitle(value)}
      >
        {formatTimestamp(value)}
      </span>
    );
  }

  if (Array.isArray(value)) {
    if (value.length && typeof value[0] === "object") {
      return <JsonCell value={value} />;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {value.slice(0, 3).map((item, index) => (
          <Badge key={index} variant="secondary" className="text-xs">
            {String(item)}
          </Badge>
        ))}
        {value.length > 3 && (
          <Badge variant="outline" className="text-xs">
            +{value.length - 3}
          </Badge>
        )}
      </div>
    );
  }

  if (typeof value === "object") return <JsonCell value={value} />;

  if (typeof value === "number") {
    return (
      <span className="tabular-nums text-sm">
        {value.toLocaleString("fr-FR")}
      </span>
    );
  }

  const text = String(value);

  if (isStatusColumn(columnName)) {
    return (
      <Badge
        variant="outline"
        color={statusTone(text)}
        className="whitespace-nowrap"
      >
        {text.replace(/_/g, " ")}
      </Badge>
    );
  }

  if (
    (columnName === "id" || columnName.endsWith("_id")) &&
    looksLikeId(text)
  ) {
    return <IdCell column={columnName} value={text} />;
  }

  if (text.length > 60) {
    return (
      <span className="text-sm" title={text}>
        {text.slice(0, 60)}…
      </span>
    );
  }

  return <span className="text-sm whitespace-nowrap">{text}</span>;
}

/** Put the columns people scan for first, and the audit plumbing last. */
export function orderColumns(names: string[]): string[] {
  const leading = LEADING_COLUMNS.filter((name) => names.includes(name));
  const trailing = TRAILING_COLUMNS.filter((name) => names.includes(name));
  const middle = names.filter(
    (name) => !leading.includes(name) && !trailing.includes(name),
  );
  return [...leading, ...middle, ...trailing];
}

/** Which columns start hidden: the entity's own list plus the audit plumbing. */
export function defaultHiddenColumns(schema: AdminEntitySchema): string[] {
  return Array.from(
    new Set([...DEFAULT_HIDDEN_COLUMNS, ...(schema.hidden_columns || [])]),
  ).filter((name) => name in schema.columns);
}

/**
 * Turn a table schema into the grid's columns. `extraColumns` is merged first
 * and wins on id collision, so a page can override one cell (an action menu,
 * a richer renderer) without giving up the generated ones.
 */
export function generateColumns(
  schema: AdminEntitySchema,
  extraColumns: ColumnDef<AdminRow>[] = [],
): ColumnDef<AdminRow>[] {
  const overridden = new Set(
    extraColumns.map((column) => column.id).filter(Boolean) as string[],
  );

  const generated = orderColumns(Object.keys(schema.columns))
    .filter((name) => !overridden.has(name))
    .map((name): ColumnDef<AdminRow> => {
      const type = schema.columns[name];
      return {
        id: name,
        accessorKey: name,
        header: humanizeColumn(name),
        enableSorting: true,
        enableHiding: name !== schema.id_column,
        cell: ({ row }: CellContext<AdminRow, unknown>) =>
          renderCell(name, type, row.original[name]),
      };
    });

  return [...extraColumns, ...generated];
}
